import { GetProductsQuery } from '@kimuno/api-sdk/lib/services/product';
import { SearchFilters } from '../types/search';
import { formatNumberRanges } from '../components/AIAssistant/shared/utils';

/**
 * Profile → dashboard-filter foundation for PRO-4.
 *
 * Single source of truth so a subject/Studyflix row's **title**, its **displayed
 * results**, and its **"Alle anzeigen"** navigation all derive from the exact same
 * effective filter set. Pure and UI-free — no React, no dispatch.
 */

// School stages the user picks (see `types/User.ts` `schoolStages` / ampli
// "school stage"). Mapped to the concrete class years the products query expects.
// `Sonstiges` has no year mapping. Source of truth for the ranges is the
// search-schema class-year set; the mapping itself is dashboard-local by decision
// (no new cross-package dependency in apps/web).
export const SCHOOL_STAGE_CLASS_YEARS: Record<string, number[]> = {
  Primarstufe: [1, 2, 3, 4],
  'Sekundarstufe I': [5, 6, 7, 8, 9, 10],
  'Sekundarstufe II': [11, 12, 13],
  Sonstiges: [],
};

// Implied class-year ranges of the search-schema PRODUCT_ITEM_SCHOOL_TYPES
// values, used only to detect a stale profile schoolType that contradicts the
// user's selected school stages (schoolType is registration-era and not
// editable in Mein Profil). Dashboard-local by the same decision as
// SCHOOL_STAGE_CLASS_YEARS above.
export const SCHOOL_TYPE_CLASS_YEARS: Record<string, number[]> = {
  Grundschule: [1, 2, 3, 4],
  'Mittlere Schulformen': [5, 6, 7, 8, 9, 10],
  Gymnasium: [5, 6, 7, 8, 9, 10, 11, 12, 13],
  // Vocational schools span grades 10/11–13 per the search-schema comment on
  // PRODUCT_ITEM_SCHOOL_TYPES — year 10 must count as an overlap.
  'Berufliche Schulen': [10, 11, 12, 13],
};

// Unknown school types have no implied range to check against, so they never
// conflict (pass-through, matching the pre-guard behavior).
const schoolTypeConflictsWithYears = (schoolType: string, classYears: number[]): boolean => {
  const impliedYears = SCHOOL_TYPE_CLASS_YEARS[schoolType];
  if (!impliedYears) return false;
  return !impliedYears.some((year) => classYears.includes(year));
};

/**
 * Union the class years of every selected school stage, deduped and ascending.
 * Unknown stage strings contribute nothing.
 */
export const mapSchoolStagesToClassYears = (stages: string[] | undefined | null): number[] => {
  if (!stages?.length) return [];
  const years = new Set<number>();
  for (const stage of stages) {
    for (const year of SCHOOL_STAGE_CLASS_YEARS[stage] ?? []) {
      years.add(year);
    }
  }
  return [...years].sort((a, b) => a - b);
};

// Class-year range separators for a title breadcrumb, e.g. [1,2,3,4,11,12,13] →
// "1–4 · 11–13" (en dash within a range, middot between ranges).
const CLASS_YEAR_RANGE_SEPARATORS = { rangeSeparator: '–', joinSeparator: ' · ' } as const;

export const formatClassYearRange = (classYears: number[]): string =>
  formatNumberRanges(classYears, CLASS_YEAR_RANGE_SEPARATORS);

/**
 * A single fallback level of the cascade. Levels are ordered strictest-first.
 * `level` is a stable id for the filter combination (not an array index), so it
 * survives absent filters being skipped and can safely encode a cache key.
 */
export enum FallbackLevel {
  SubjectYearsType = 0,
  SubjectYears = 1,
  Subject = 2,
}

export interface ProfileRowInput {
  // One subject id (subject row) or several combined into a single query
  // (the Studyflix row spans the user's top subjects). The relax cascade only
  // ever touches the profile filters — the subject set is fixed at every level.
  subject: string | string[];
  // Class years already mapped from school stages (may be empty).
  classYears: number[];
  // Raw `user.schoolType` — passed through; dropped when falsy or when its
  // implied class-year range (SCHOOL_TYPE_CLASS_YEARS) does not intersect
  // `classYears`. NOT normalized (decision: pass-through). Must match a
  // search-schema PRODUCT_ITEM_SCHOOL_TYPES value server-side; divergence is a
  // follow-up, not handled here.
  schoolType?: string | null;
  // Publisher filter that persists across every fallback level (Studyflix row).
  publisher?: string | null;
}

// Title segments for the effective level — the localized `Neu für dich:` prefix
// and `Kl.` label are added by the rendering component (Phase 2), not here.
export interface TitleSegments {
  subject: string;
  schoolType?: string;
  classYearRange?: string;
}

export interface CascadeLevel {
  level: FallbackLevel;
  query: GetProductsQuery;
  titleSegments: TitleSegments;
  searchFilters: SearchFilters;
  keyPrefix: string;
}

const hasValue = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const toSubjectIds = (subject: string | string[]): string[] =>
  Array.isArray(subject) ? subject : [subject];

/**
 * Serialize the full effective filter set into a stable cache-key prefix. Fed to
 * the existing reducer key logic (`${keyPrefix}-${subjects.join(',')}`) so each
 * fallback level caches under its own key — distinct filter sets never collide and
 * a level change never reads a stale result from a different level.
 */
export const buildKeyPrefix = (
  input: ProfileRowInput,
  level: FallbackLevel,
  includeYears: boolean,
  includeType: boolean
): string => {
  const years = includeYears ? input.classYears.join('-') : '';
  const type = includeType && hasValue(input.schoolType) ? input.schoolType : '';
  const publisher = hasValue(input.publisher) ? input.publisher : '';
  const subject = toSubjectIds(input.subject).join(',');
  return `dash|s:${subject}|y:${years}|t:${type}|p:${publisher}|l:${level}`;
};

const buildLevel = (
  input: ProfileRowInput,
  level: FallbackLevel,
  includeYears: boolean,
  includeType: boolean
): CascadeLevel => {
  const applyYears = includeYears && input.classYears.length > 0;
  const applyType = includeType && hasValue(input.schoolType);
  const applyPublisher = hasValue(input.publisher);
  const keyPrefix = buildKeyPrefix(input, level, applyYears, applyType);
  const subjectIds = toSubjectIds(input.subject);

  const query: GetProductsQuery = {
    subjects: subjectIds,
    keyPrefix,
    ...(applyYears && { classYears: input.classYears }),
    ...(applyType && { schoolTypes: [input.schoolType as string] }),
    ...(applyPublisher && { includePublishers: input.publisher as string }),
  };

  const searchFilters: SearchFilters = {
    subject: subjectIds,
    ...(applyYears && { classYear: input.classYears.map(String) }),
    ...(applyType && { schoolType: [input.schoolType as string] }),
    ...(applyPublisher && { publisher: [input.publisher as string] }),
  };

  const titleSegments: TitleSegments = {
    subject: subjectIds.join(','),
    ...(applyType && { schoolType: input.schoolType as string }),
    ...(applyYears && { classYearRange: formatClassYearRange(input.classYears) }),
  };

  return { level, query, titleSegments, searchFilters, keyPrefix };
};

/**
 * Build the ordered fallback cascade for one row. Strictest-first per the spec:
 *   L0 subject + classYears + schoolType
 *   L1 subject + classYears
 *   L2 subject only
 *
 * Absent profile filters are omitted (a missing filter is not a relax step): the
 * cascade starts at the highest level for which the user actually has data, so
 * levels that would require a missing filter are skipped rather than duplicated.
 * The publisher filter (Studyflix) is not part of the relax cascade — it persists
 * at every level.
 */
export const buildProfileFilterCascade = (input: ProfileRowInput): CascadeLevel[] => {
  const hasYears = input.classYears.length > 0;
  const hasType =
    hasValue(input.schoolType) &&
    !schoolTypeConflictsWithYears(input.schoolType, input.classYears);

  const levels: CascadeLevel[] = [];
  if (hasYears && hasType) {
    levels.push(buildLevel(input, FallbackLevel.SubjectYearsType, true, true));
  }
  if (hasYears) {
    levels.push(buildLevel(input, FallbackLevel.SubjectYears, true, false));
  }
  levels.push(buildLevel(input, FallbackLevel.Subject, false, false));

  return levels;
};
